import type { FieldSuggestion } from '@frontend/api';
import { SUGGESTION_TYPES } from '@frontend/api';
import type { CascaderOption, CascaderSubmenu } from '@frontend/components';
import { isCascaderSubmenu } from '@frontend/components';

import type { MaterialCascaderNode } from './adaptMaterialOptionsToCascader';
import type { MaterialCascaderSuggestionOption } from './materialCascaderSuggestionTypes';

/**
 * Backend {@link FieldSuggestion} `fieldName` values for material FKs
 * (see `FieldSuggestionSerializer.FK_FIELD_MAP` in webservices).
 */
export const MATERIAL_SUGGESTION_FIELD_NAMES = new Set([
  'material_id',
  'material__mat_family_id',
]);

/** `suggestion_metadata` when `field_name` is `material_id` (MaterialSerializer). */
export type MaterialIdFkMetadata = {
  id: number;
  name: string;
  materialFamilyId: number;
};

/** `suggestion_metadata` when `field_name` is `material__mat_family_id` (MaterialFamilySerializer). */
export type MaterialFamilyFkMetadata = {
  id: number;
  name: string;
  materialClassId: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

export function asMaterialIdFkMetadata(
  value: unknown,
): MaterialIdFkMetadata | null {
  if (!isRecord(value)) return null;
  const { id, name, materialFamilyId } = value;
  if (
    typeof id !== 'number' ||
    typeof name !== 'string' ||
    typeof materialFamilyId !== 'number'
  ) {
    return null;
  }
  return { id, name, materialFamilyId };
}

export function asMaterialFamilyFkMetadata(
  value: unknown,
): MaterialFamilyFkMetadata | null {
  if (!isRecord(value)) return null;
  const { id, name, materialClassId } = value;
  if (
    typeof id !== 'number' ||
    typeof name !== 'string' ||
    typeof materialClassId !== 'number'
  ) {
    return null;
  }
  return { id, name, materialClassId };
}

/**
 * For FK suggestions, backend exposes `suggestion` as a string id (see `FieldSuggestion.suggestion`).
 */
function parseFkSuggestionId(
  suggestion: FieldSuggestion['suggestion'],
): string | null {
  if (typeof suggestion === 'number' && Number.isFinite(suggestion)) {
    return String(suggestion);
  }
  if (typeof suggestion === 'string') {
    const t = suggestion.trim();
    if (/^\d+$/.test(t)) {
      return t;
    }
  }
  return null;
}

export function filterMaterialFieldSuggestions(
  fieldSuggestions: FieldSuggestion[] | undefined,
): FieldSuggestion[] {
  if (!fieldSuggestions?.length) return [];
  return fieldSuggestions.filter(
    (s) =>
      MATERIAL_SUGGESTION_FIELD_NAMES.has(s.fieldName) &&
      s.suggestionType === SUGGESTION_TYPES.FK,
  );
}

export type MaterialSuggestionTarget =
  | { kind: 'family'; familyId: string }
  | { kind: 'material'; materialId: string };

export type MaterialSuggestionEntry = {
  fieldSuggestion: FieldSuggestion;
  target: MaterialSuggestionTarget;
};

/**
 * Every overview_bundle row the client can map to a cascader target.
 * Use this to drive Jump To / Select banners (one entry per suggestion).
 */
export function listParsableMaterialSuggestions(
  fieldSuggestions: FieldSuggestion[],
): MaterialSuggestionEntry[] {
  const out: MaterialSuggestionEntry[] = [];
  for (const fieldSuggestion of filterMaterialFieldSuggestions(
    fieldSuggestions,
  )) {
    const target = parseMaterialSuggestionTarget(fieldSuggestion);
    if (target != null) {
      out.push({ fieldSuggestion, target });
    }
  }
  return out;
}

export function materialSuggestionTargetKey(
  target: MaterialSuggestionTarget,
): string {
  return target.kind === 'family'
    ? `family:${target.familyId}`
    : `material:${target.materialId}`;
}

/**
 * PartBot / print extraction can return multiple {@link FieldSuggestion} rows for the same
 * material or family; banners should show at most one row per cascader target.
 */
export function dedupeMaterialSuggestionEntries(
  entries: MaterialSuggestionEntry[],
): MaterialSuggestionEntry[] {
  const seen = new Set<string>();
  const out: MaterialSuggestionEntry[] = [];
  for (const entry of entries) {
    const key = materialSuggestionTargetKey(entry.target);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(entry);
  }
  return out;
}

export function parseMaterialSuggestionTarget(
  suggestion: FieldSuggestion,
): MaterialSuggestionTarget | null {
  if (suggestion.suggestionType !== SUGGESTION_TYPES.FK) {
    return null;
  }

  if (suggestion.fieldName === 'material__mat_family_id') {
    const fromSuggestion = parseFkSuggestionId(suggestion.suggestion);
    const meta = asMaterialFamilyFkMetadata(suggestion.suggestionMetadata);
    const familyId = fromSuggestion ?? (meta != null ? String(meta.id) : null);
    if (familyId == null || familyId === '') {
      return null;
    }
    return { kind: 'family', familyId };
  }

  if (suggestion.fieldName === 'material_id') {
    const fromSuggestion = parseFkSuggestionId(suggestion.suggestion);
    const meta = asMaterialIdFkMetadata(suggestion.suggestionMetadata);
    const materialId =
      fromSuggestion ?? (meta != null ? String(meta.id) : null);
    if (materialId == null || materialId === '') {
      return null;
    }
    return { kind: 'material', materialId };
  }

  return null;
}

export function findMaterialNode(
  materialClasses: MaterialCascaderNode[],
  materialId: string,
): MaterialCascaderNode | undefined {
  for (const matClass of materialClasses) {
    for (const family of matClass.children ?? []) {
      for (const material of family.children ?? []) {
        if (String(material.value) === materialId) {
          return material;
        }
      }
    }
  }
  return undefined;
}

/** `[classId, familyId, materialId]` for a global material, or `null` if not in tree. */
export function findMaterialRowPathInNodes(
  materialClasses: MaterialCascaderNode[],
  materialId: string,
): string[] | null {
  const mid = String(materialId);
  for (const matClass of materialClasses) {
    for (const family of matClass.children ?? []) {
      for (const material of family.children ?? []) {
        if (String(material.value) === mid) {
          return [String(matClass.value), String(family.value), mid];
        }
      }
    }
  }
  return null;
}

export function findFamilyValuePathInNodes(
  materialClasses: MaterialCascaderNode[],
  familyId: string,
): string[] | null {
  const fid = String(familyId);
  for (const cls of materialClasses) {
    for (const fam of cls.children ?? []) {
      if (String(fam.value) === fid) {
        return [String(cls.value), fid];
      }
    }
  }
  return null;
}

/**
 * Value path to commit for a material suggestion (includes first supplier tier when present).
 */
export function getMaterialSuggestionCommitPath(
  materialClasses: MaterialCascaderNode[],
  target: MaterialSuggestionTarget,
): string[] | null {
  if (target.kind === 'family') {
    return null;
  }
  const base = findMaterialRowPathInNodes(materialClasses, target.materialId);
  if (!base?.length) {
    return null;
  }
  const matNode = findMaterialNode(materialClasses, target.materialId);
  const children = matNode?.children ?? [];
  if (children.length > 0) {
    const first = children[0];
    return [...base, String(first.value)];
  }
  return base;
}

/**
 * Value path to drill the menu to for "Jump to" (family branch or material row when suppliers exist).
 */
export function getMaterialSuggestionJumpPath(
  materialClasses: MaterialCascaderNode[],
  target: MaterialSuggestionTarget,
): string[] | null {
  if (target.kind === 'family') {
    return findFamilyValuePathInNodes(materialClasses, target.familyId);
  }
  const base = findMaterialRowPathInNodes(materialClasses, target.materialId);
  if (!base?.length) {
    return null;
  }
  const matNode = findMaterialNode(materialClasses, target.materialId);
  const hasSupplierTier = (matNode?.children?.length ?? 0) > 0;
  if (hasSupplierTier) {
    return base;
  }
  return null;
}

export function getMaterialSuggestionBannerTitle(
  entry: MaterialSuggestionEntry,
): string {
  const { fieldSuggestion, target } = entry;
  if (target.kind === 'family') {
    const meta = asMaterialFamilyFkMetadata(fieldSuggestion.suggestionMetadata);
    if (meta?.name) {
      return meta.name;
    }
    return `Family ${target.familyId}`;
  }
  const meta = asMaterialIdFkMetadata(fieldSuggestion.suggestionMetadata);
  if (meta?.name) {
    return meta.name;
  }
  return `Material ${target.materialId}`;
}

type DecorationHint =
  | { path: string[]; leaf: 'familyBranch' }
  | { path: string[]; leaf: 'materialSuggestion' }
  | { path: string[]; leaf: 'materialBranch' };

function resolveDecorationHint(
  materialClasses: MaterialCascaderNode[],
  target: MaterialSuggestionTarget,
): DecorationHint | null {
  if (target.kind === 'family') {
    const path = findFamilyValuePathInNodes(materialClasses, target.familyId);
    if (!path?.length) return null;
    return { path, leaf: 'familyBranch' };
  }

  const path = findMaterialRowPathInNodes(materialClasses, target.materialId);
  if (!path?.length) return null;

  const matNode = findMaterialNode(materialClasses, target.materialId);
  const hasSupplierTier = (matNode?.children?.length ?? 0) > 0;

  if (hasSupplierTier) {
    return { path, leaf: 'materialBranch' };
  }
  return { path, leaf: 'materialSuggestion' };
}

function setSuggestionFlagsOnPath(
  root: CascaderSubmenu<MaterialCascaderSuggestionOption>,
  hint: DecorationHint,
): void {
  const path = hint.path;
  if (!path.length) return;

  let levelOptions = root.children.flatMap((g) => g.children);

  for (let depth = 0; depth < path.length; depth++) {
    const seg = path[depth];
    const node = levelOptions.find((o) => o.value === seg);
    if (!node) return;

    const isLast = depth === path.length - 1;

    if (isLast) {
      if (hint.leaf === 'familyBranch') {
        (node as MaterialCascaderSuggestionOption).isSuggestedBranch = true;
      } else if (hint.leaf === 'materialBranch') {
        (node as MaterialCascaderSuggestionOption).isSuggestedBranch = true;
      } else if (hint.leaf === 'materialSuggestion') {
        (node as MaterialCascaderSuggestionOption).isSuggestion = true;
      }
      return;
    }

    if (!isCascaderSubmenu(node)) return;
    const groups = node.children as Array<{
      children: MaterialCascaderSuggestionOption[];
    }>;
    levelOptions = groups.flatMap((g) => g.children);
  }
}

export function buildDecoratedMaterialSubmenu({
  cascaderSubmenu,
  materialClasses,
  fieldSuggestions,
}: {
  cascaderSubmenu: CascaderSubmenu<CascaderOption>;
  materialClasses: MaterialCascaderNode[];
  fieldSuggestions: FieldSuggestion[];
}): CascaderSubmenu<MaterialCascaderSuggestionOption> {
  const entries = listParsableMaterialSuggestions(fieldSuggestions);
  if (!entries.length) {
    return cascaderSubmenu as CascaderSubmenu<MaterialCascaderSuggestionOption>;
  }

  const cloned = JSON.parse(
    JSON.stringify(cascaderSubmenu),
  ) as CascaderSubmenu<MaterialCascaderSuggestionOption>;

  for (const { target } of entries) {
    const hint = resolveDecorationHint(materialClasses, target);
    if (hint) {
      setSuggestionFlagsOnPath(cloned, hint);
    }
  }

  return cloned;
}
